import { useMutation, useQueryClient } from '@tanstack/react-query';
import { lacTenantService } from '../../../services/LacTenantService';
import type {
  LacCreateTenantInput,
  LacUpdateTenantInput,
} from '../../../types/lac';
import { lacQueryKeys } from '../lacQueryKeys';

function invalidateLacTenantOverviews(
  queryClient: ReturnType<typeof useQueryClient>,
  tenantName?: string,
) {
  void queryClient.invalidateQueries({
    queryKey: [...lacQueryKeys.all, 'tenantOverviews'],
  });
  if (tenantName) {
    void queryClient.invalidateQueries({
      queryKey: [...lacQueryKeys.all, 'tenantOverview', tenantName],
    });
  }
}

export function useCreateLacTenantMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LacCreateTenantInput) =>
      lacTenantService.createTenant(input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenants(),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      invalidateLacTenantOverviews(queryClient, input.name);
    },
  });
}

export function useUpdateLacTenantMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: LacUpdateTenantInput) =>
      lacTenantService.updateTenant(input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenants(),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenant(input.tenantName),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      invalidateLacTenantOverviews(queryClient, input.tenantName);
    },
  });
}

export function useSetLacTenantActiveMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantName,
      active,
    }: {
      tenantName: string;
      active: boolean;
    }) => lacTenantService.setTenantActive(tenantName, active),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenants(),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenant(variables.tenantName),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      invalidateLacTenantOverviews(queryClient, variables.tenantName);
    },
  });
}

export function useDeleteLacTenantMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantName: string) =>
      lacTenantService.deleteTenant(tenantName),
    onSuccess: (_result, tenantName) => {
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.tenants(),
      });
      void queryClient.removeQueries({
        queryKey: lacQueryKeys.tenant(tenantName),
      });
      void queryClient.removeQueries({
        queryKey: lacQueryKeys.tenantOverview(tenantName),
      });
      void queryClient.invalidateQueries({
        queryKey: lacQueryKeys.usersByTenant(),
      });
      invalidateLacTenantOverviews(queryClient);
    },
  });
}
